// AcceptanceMailDialog — send-by-email dialog reused for both CU1 (form/FORM) and CU2
// (signed/SIGNED). "To" recipients only, no CC/BCC (RN3); subject/body editable, pre-filled with
// fixed-English defaults (D7, `acceptanceMailTemplates`). Validates client-side before allowing
// Send (RN3/RN4 — non-empty/well-formed recipients, non-empty subject) and warns before sending
// if the known attachment size exceeds the configured maximum (D10/S16). On success shows an
// inline confirmation (S1/S8); on failure shows a retryable error, distinguishing the mail size
// rejection (S17) from any other failure (S15).
//
// RN2 — a Preview action opens the exact file(s) that will be attached (read-only, no send) in the
// shared viewer. A FORM mail carries one attachment (the generated acceptance PDF), so a single
// Preview button is enough. A SIGNED mail carries the whole active upload batch — the signed PDF
// plus the evidences uploaded with it — so the dialog lists them and previews any of them: the
// sender sees the full set that travels instead of assuming only the signed PDF does.
import { useEffect, useState } from 'react'
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  List, ListItem, ListItemText, Paper, TextField, Typography
} from '@mui/material'
import { VisibilityRounded } from '@mui/icons-material'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { apiPost } from '../generic/Api_Functions'
import { ACCEPTANCE_MAIL_DOCUMENT_TYPES, buildAcceptanceMailDefaults } from './acceptanceMailTemplates'
import { fetchActiveUploadSet, totalSizeBytesOf } from './acceptanceUploadSet'
import SignedDocumentViewerDialog from './SignedDocumentViewerDialog'

const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
// GATE 3 fix (S17) — Sun's fabricationorderacceptance module catches the mail micro-service's 413
// and re-maps it to a 400 with this exact message (`onMailError` in `FabricationOrderAcceptanceRest`,
// `AcceptanceMailAttachmentTooLarge`); the 413 itself never reaches Jupiter for this endpoint, so
// the discriminant is the message, not the HTTP status.
const ATTACHMENT_TOO_LARGE_MESSAGE = 'Maximum attachment size exceeded'

// Same document-type labels the send history uses for its rows.
const PREVIEW_TITLE_KEYS = {
  [ACCEPTANCE_MAIL_DOCUMENT_TYPES.FORM]: 'Acceptance form',
  [ACCEPTANCE_MAIL_DOCUMENT_TYPES.SIGNED]: 'Signed document'
}

// Same document-type labels the uploaded-documents list uses, so a SIGNED mail's attachment list
// shows which file is the signed document and which are evidences.
const KIND_LABEL_KEYS = {
  SIGNED: 'Signed document',
  EVIDENCE: 'Evidence'
}

// The FORM attachment is the same server-generated acceptance PDF `GET {id}/pdf` returns (RN2).
const formPreviewContentPath = (fabricationOrderId) => `fabricationOrderAcceptance/${fabricationOrderId}/pdf`

// Each document of a SIGNED batch is served by id through the per-document content endpoint (the
// same one the uploaded-documents list uses — it serves evidences too, not only the signed PDF).
const documentPreviewContentPath = (fabricationOrderId, documentId) =>
  `fabricationOrderAcceptance/${fabricationOrderId}/documents/${documentId}/content?download=false`

const isValidEmail = (email) => EMAIL_FORMAT.test(email)

export default function AcceptanceMailDialog ({
  open,
  documentType,
  fabricationOrderId,
  fabricationOrderCode = '',
  vehicleName = '',
  versionNumber = null,
  attachmentSizeBytes = false,
  maxAttachmentSizeBytes = false,
  onClose = () => {},
  onSent = () => {}
}) {
  const { t_ } = useSmartTranslation()

  const [recipients, setRecipients] = useState([])
  const [recipientInput, setRecipientInput] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const [sent, setSent] = useState(false)
  // The document being previewed; {} = none. For a FORM mail the single entry is synthesized below.
  const [previewing, setPreviewing] = useState({})
  // RN2 — the active upload batch a SIGNED mail attaches; always [] for a FORM mail.
  const [attachments, setAttachments] = useState([])

  const isSignedMail = documentType === ACCEPTANCE_MAIL_DOCUMENT_TYPES.SIGNED

  // Reset to a fresh dialog (defaults, no recipients, no leftover error/confirmation) every time
  // it opens — including when it is reopened to resend to different recipients (S5).
  useEffect(() => {
    if (!open) return
    const defaults = buildAcceptanceMailDefaults(documentType, fabricationOrderCode, vehicleName, versionNumber)
    setRecipients([])
    setRecipientInput('')
    setSubject(defaults.subject)
    setBody(defaults.body)
    setSending(false)
    setError('')
    setSent(false)
    setPreviewing({})
  }, [open, documentType, fabricationOrderCode, vehicleName, versionNumber])

  // RN2 — resolved on open (not on mount): the batch changes as the operator uploads or deletes
  // documents behind this dialog, and what is listed must be what the send will actually attach.
  useEffect(() => {
    if (!open || !isSignedMail) {
      setAttachments([])
      return
    }
    let isCurrent = true
    fetchActiveUploadSet(fabricationOrderId).then((uploadSet) => {
      if (isCurrent) setAttachments(uploadSet)
    })
    return () => {
      isCurrent = false
    }
  }, [open, isSignedMail, fabricationOrderId])

  const addRecipient = () => {
    const value = recipientInput.trim()
    if (!value) return
    setRecipients((prev) => (prev.includes(value) ? prev : [...prev, value]))
    setRecipientInput('')
  }

  const removeRecipient = (email) =>
    setRecipients((prev) => prev.filter((recipient) => recipient !== email))

  // D10/S16 — the warning must weigh what actually travels: for a SIGNED mail that is the whole
  // batch, not just the signed document (`attachmentSizeBytes`, which the caller derives from
  // `signedDocumentSizeBytes`). Falls back to the caller's value until the batch has loaded.
  const effectiveAttachmentSizeBytes = (isSignedMail && attachments.length > 0)
    ? totalSizeBytesOf(attachments)
    : attachmentSizeBytes
  const sizeExceeded = Boolean(effectiveAttachmentSizeBytes) && Boolean(maxAttachmentSizeBytes) &&
    effectiveAttachmentSizeBytes > maxAttachmentSizeBytes
  const recipientsValid = recipients.length > 0 && recipients.every(isValidEmail)
  const subjectValid = subject.trim() !== ''
  const canSend = recipientsValid && subjectValid && !sizeExceeded && !sending

  const handleSend = () => {
    if (!canSend) return
    setSending(true)
    setError('')
    apiPost(`fabricationOrderAcceptance/${fabricationOrderId}/send-email`, {
      documentType,
      recipients,
      subject,
      body
    })
      .then((dispatch) => {
        setSent(true)
        onSent(dispatch)
      })
      .catch((err) => {
        if (err && err.message === ATTACHMENT_TOO_LARGE_MESSAGE) {
          setError(t_('The email service rejected the attachment for exceeding the maximum size allowed.'))
        } else {
          setError(t_('The email could not be sent. You can try again.'))
        }
      })
      .finally(() => setSending(false))
  }

  const handleClose = () => onClose()

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle>
        {documentType === 'SIGNED' ? t_('Send signed document by email') : t_('Send form by email')}
      </DialogTitle>
      <DialogContent>
        {sent
          ? (
            <Alert severity='success'>{t_('Email sent')}</Alert>
            )
          : (
            <>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1 }}>
                <TextField
                  label={t_('Add recipient')}
                  size='small'
                  fullWidth
                  value={recipientInput}
                  onChange={(e) => setRecipientInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addRecipient()
                    }
                  }}
                />
                <Button onClick={addRecipient} variant='outlined'>{t_('Add')}</Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                {recipients.map((email) => (
                  <Chip
                    key={email}
                    label={email}
                    onDelete={() => removeRecipient(email)}
                    color={isValidEmail(email) ? 'default' : 'error'}
                  />
                ))}
              </Box>
              {recipients.length > 0 && !recipientsValid && (
                <Typography variant='body2' color='error' sx={{ mb: 1.5 }}>
                  {t_('Invalid email format')}
                </Typography>
              )}

              <TextField
                label={t_('Subject')}
                fullWidth
                size='small'
                sx={{ mb: 1.5 }}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
              <TextField
                label={t_('Body')}
                fullWidth
                multiline
                minRows={3}
                size='small'
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />

              {/* RN2 — what will travel, listed so the sender can check and preview every file. */}
              {isSignedMail && attachments.length > 0 && (
                <Box data-testid='mail-attachments' sx={{ mt: 1.5 }}>
                  <Typography variant='body2' color='text.secondary' sx={{ mb: 0.5 }}>
                    {t_('Attachments')}
                  </Typography>
                  <Paper variant='outlined'>
                    <List dense disablePadding>
                      {attachments.map((attachment) => (
                        <ListItem
                          key={attachment.id}
                          secondaryAction={
                            <Button
                              size='small' startIcon={<VisibilityRounded />}
                              onClick={() => setPreviewing(attachment)}
                            >
                              {t_('Preview')}
                            </Button>
                          }
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <span>{attachment.originalFilename}</span>
                                {attachment.kind && (
                                  <Chip size='small' label={t_(KIND_LABEL_KEYS[attachment.kind] || attachment.kind)} />
                                )}
                              </Box>
                            }
                            primaryTypographyProps={{ component: 'div' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Box>
              )}

              {sizeExceeded && (
                <Alert severity='warning' sx={{ mt: 1.5 }}>
                  {t_('The attachment exceeds the maximum size allowed ({{maxSize}}).', { maxSize: maxAttachmentSizeBytes })}
                </Alert>
              )}

              {error && (
                <Alert severity='error' sx={{ mt: 1.5 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}
            </>
            )}
      </DialogContent>
      <DialogActions>
        {sent
          ? <Button onClick={handleClose}>{t_('Close')}</Button>
          : (
            <>
              {/* A SIGNED mail previews per attachment from the list above; a FORM mail has a
                  single generated PDF, so it keeps one Preview action here. */}
              {!isSignedMail && (
                <Button
                  onClick={() => setPreviewing({ id: 'form', originalFilename: '' })} disabled={sending}
                  startIcon={<VisibilityRounded />} sx={{ mr: 'auto' }}
                >
                  {t_('Preview')}
                </Button>
              )}
              <Button onClick={handleClose} disabled={sending}>{t_('Cancel')}</Button>
              <Button
                variant='contained' disabled={!canSend} onClick={handleSend}
                startIcon={sending ? <CircularProgress size={16} color='inherit' /> : null}
              >
                {sending ? `${t_('Sending')}...` : t_('Send')}
              </Button>
            </>
            )}
      </DialogActions>

      {/* Read-only preview of an exact attachment the send will produce; never sends. A SIGNED
          attachment is titled with its own filename (there are several); the FORM one keeps the
          document-type label, since its generated filename is not meaningful to the sender. */}
      <SignedDocumentViewerDialog
        open={Boolean(previewing.id)}
        onClose={() => setPreviewing({})}
        title={isSignedMail
          ? previewing.originalFilename || ''
          : t_(PREVIEW_TITLE_KEYS[documentType] || PREVIEW_TITLE_KEYS[ACCEPTANCE_MAIL_DOCUMENT_TYPES.FORM])}
        contentPath={previewing.id
          ? (isSignedMail
              ? documentPreviewContentPath(fabricationOrderId, previewing.id)
              : formPreviewContentPath(fabricationOrderId))
          : ''}
      />
    </Dialog>
  )
}
